import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Rating,
    TextField,
    Typography,
    CircularProgress
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    customerPageStyling,
    useStyles
} from 'src/theme/vemigoCustomStyles/VemigoCustomStyles';
import VemigoGrid from 'src/components/VemigoGrid';
import { CustomerCarService } from 'src/services/customer/customerCarService';
import { CustomerAddresseServicess } from 'src/services/customer/addresseServices';
import { ServicePartnerDetailsServices } from 'src/services/servicePartner/sPDetailsService';
import { FieldAgentServices } from 'src/services/servicePartner/fieldAgentService';
import { ServiceOrderModel } from 'src/models/customer/bookingModel';
import { useCustomerData } from 'src/content/pages/customer/customerData';
import BookingService from 'src/services/booking/bookingServices';

interface NotificationReviewDialogProps {
    open: boolean;
    onClose: () => void;
    serviceOrderId: number;
}

export const NotificationReviewDialog = ({
    open,
    onClose,
    serviceOrderId
}: NotificationReviewDialogProps) => {
    const { allCities, allServices, allTimeSlots, allStatuses } = useCustomerData();

    const [loading, setLoading] = useState(true);
    const [serviceOrderData, setServiceOrderData] = useState<ServiceOrderModel | null>(null);

    const [carName, setCarName] = useState<string | null>(null);
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [areaName, setAreaName] = useState<string | null>(null);
    const [servicePartner, setServicePartner] = useState<any[]>([]);
    const [fieldAgent, setFieldAgent] = useState<any[]>([]);

    useEffect(() => {
        if (open && serviceOrderId) {
            fetchAllData();
        }
    }, [open, serviceOrderId]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const orderData = await BookingService.getServiceOrderById(serviceOrderId);
            setServiceOrderData(orderData);

            if (orderData) {
                try {
                    const carData = await CustomerCarService.getCarData(orderData.customerCarId);
                    setCarName(carData?.car?.name || 'No car');
                } catch {
                    setCarName('No car');
                }

                try {
                    const address = await CustomerAddresseServicess.getAddressData(orderData.customerAddressId);
                    setAddressLine1(address.addressLine1 || '');
                    setAddressLine2(address.addressLine2 || '');
                    setAreaName(address.area?.name || null);
                } catch {
                    setAddressLine1('');
                    setAddressLine2('');
                    setAreaName(null);
                }

                try {
                    const spData = await ServicePartnerDetailsServices.getAllData();
                    setServicePartner(spData);
                } catch {
                    setServicePartner([]);
                }

                try {
                    const faData = await FieldAgentServices.getAllData(orderData.servicePartnerId);
                    setFieldAgent(faData);
                } catch {
                    setFieldAgent([]);
                }
            }
        } catch (error) {
            console.error('Error fetching notification review data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogContent style={{ textAlign: 'center', padding: '40px' }}>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }

    if (!serviceOrderData) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogContent style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography>No data found</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const cityId = serviceOrderData.cityId;
    const cityName = allCities.find((city) => city.id === cityId)?.name || 'N/A';

    const columns = [
        { field: 'serviceName', headerName: 'Service Name', flex: 1 },
        { field: 'date', headerName: 'Date', flex: 0.8 },
        { field: 'timeSlot', headerName: 'Time Slot', flex: 1 },
        { field: 'servicePartner', headerName: 'Service Partner', flex: 1 },
        { field: 'fieldAgent', headerName: 'Field Agent', flex: 1 },
        { field: 'completeStatusId', headerName: 'Complete Status', flex: 1 }
    ];

    const serviceRows = [
        {
            id: serviceOrderData.serviceId,
            serviceName: allServices.find((x) => x.id === serviceOrderData.serviceId)?.name || 'N/A',
            date: serviceOrderData.serviceDate?.split('T')[0] ?? '',
            timeSlot: allTimeSlots?.find((x) => x.id === serviceOrderData?.timeSlotId)?.startTime ?? '00:00',
            servicePartner: servicePartner.find((x) => x.id === serviceOrderData.servicePartnerId)?.businessName || 'N/A',
            fieldAgent: Array.isArray(serviceOrderData.fieldAgents) && serviceOrderData.fieldAgents.length > 0
                ? (serviceOrderData.fieldAgents as any[]).map(agent =>
                    agent && typeof agent === 'object'
                        ? `${agent.firstName || ''} ${agent.lastName || ''}`.trim()
                        : 'Invalid Agent'
                ).filter(name => name).join(', ')
                : 'Not Assigned',
            completeStatusId: allStatuses?.find((st) => st.id === serviceOrderData.currentStatus)?.name || '',
        }
    ];

    const allFields = [
        {
            id: 'serviceOrderId',
            field: 'textField',
            title: 'Service Order ID',
            type: 'text',
            size: 6,
            value: serviceOrderData.bookingServiceId,
        },
        {
            id: 'createdDateTime',
            field: 'textField',
            title: 'Booking Date & Time',
            type: 'text',
            value: serviceOrderData.serviceDate?.split('T')[0] || '',
            size: 6,
        },
        {
            id: 'customerName',
            field: 'textField',
            title: 'Customer Name',
            type: 'text',
            size: 6,
            value: `${serviceOrderData.firstAndMiddleName} ${serviceOrderData.lastName}` || 'N/A',
        },
        {
            id: 'customerCarId',
            field: 'textField',
            title: 'Customer Car Name',
            type: 'text',
            size: 6,
            value: carName || 'N/A',
        },
        {
            id: 'serviceId',
            field: 'grid',
            title: 'Service Name',
            size: 12,
        }
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md">
            <DialogTitle style={useStyles.dialogTitle}>Review & Rating Details</DialogTitle>
            <DialogContent style={useStyles.bodyStyle}>
                <Grid container spacing={2}>
                    {allFields.map((field) => (
                        <Grid item xs={field.field !== 'grid' ? 6 : 12} key={field.id}>
                            <Typography style={useStyles.formTitle}>
                                {field.title}
                            </Typography>
                            {field.field === 'textField' && (
                                <TextField
                                    style={useStyles.formControl}
                                    id={field.id}
                                    type="text"
                                    value={field.value}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                    variant="filled"
                                />
                            )}
                            {field.field === 'grid' && (
                                <Grid item xs={12}>
                                    <VemigoGrid rows={serviceRows} columns={columns} />
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Grid>

                <Typography variant="h6" style={{ margin: '5% 0 2% 0' }}>
                    Customer Addresses & Mobile Number
                </Typography>
                <div
                    key="selectedCar-Info"
                    style={customerPageStyling.selects.car.container}
                >
                    <table style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: '20px 10px'
                    }}>
                        <tbody>
                            <tr>
                                <td>
                                    <b>
                                        {cityName}
                                        <br />- {areaName || 'N/A'}
                                    </b>
                                </td>
                                <td>Address Type</td>
                                <td>
                                    {addressLine1 || ''}-<br />
                                    {addressLine2 || ''}
                                </td>
                                <td>{serviceOrderData.mobileNumber || 'N/A'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Typography variant="h6" style={{ margin: '5% 0 2% 0' }}>
                    Customer Review & Rating
                </Typography>
                <div style={customerPageStyling.selects.car.container}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ margin: '5% 5%', fontSize: '16px' }}>
                            Rating:
                        </span>
                        <Rating
                            name="rating-control"
                            value={serviceOrderData.serviceOrderReview?.rating || 0}
                            precision={1}
                            readOnly
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ margin: '8% 5%', fontSize: '16px' }}>
                            Review:
                        </span>
                        <TextField
                            style={{ backgroundColor: 'white', width: '500px' }}
                            multiline
                            rows={2}
                            value={serviceOrderData.serviceOrderReview?.review || 'No review provided'}
                            InputProps={{ readOnly: true }}
                            variant="filled"
                        />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};